import * as React from 'react';
import { X } from 'lucide-react';
import { Badge, RelationPicker } from '@aimmo/ui';
import type { FilterOption } from '@aimmo/ui';

export interface CriteriaPickerProps {
  value: unknown;
  onChange: (value: string[]) => void;
  disabled: boolean;
  search: (query: string, signal?: AbortSignal) => Promise<readonly FilterOption[]>;
  resolve?: (value: string) => Promise<FilterOption | null>;
  placeholder?: string;
  /** The id the field's label points at, so the search is named by it. */
  id?: string;
}

/**
 * A set of related ids — the target comuni, zones and typologies of a demand.
 *
 * The kit's relation field holds one value, and a search profile is a list. This
 * wraps the same picker: choosing adds a chip and clears the control, so the
 * next choice starts from an empty search rather than from the previous answer.
 *
 * Labels are resolved once per id and cached here, so removing one chip does not
 * re-fetch the rest.
 */
export function CriteriaPicker({
  value,
  onChange,
  disabled,
  search,
  resolve,
  placeholder,
  id,
}: CriteriaPickerProps) {
  const selected = React.useMemo(() => (Array.isArray(value) ? (value as string[]) : []), [value]);
  const [labels, setLabels] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (!resolve) return;
    const missing = selected.filter((id) => !labels[id]);
    if (missing.length === 0) return;

    let cancelled = false;
    void Promise.all(missing.map((id) => resolve(id))).then((options) => {
      if (cancelled) return;
      setLabels((current) => {
        const next = { ...current };
        options.forEach((option, index) => {
          // An id the user cannot resolve reads as itself rather than as a gap.
          next[missing[index]!] = option?.label ?? missing[index]!;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [selected, labels, resolve]);

  const add = (id: string | null) => {
    if (!id || selected.includes(id)) return;
    onChange([...selected, id]);
  };

  return (
    <div className="grid gap-2">
      <RelationPicker
        id={id}
        value={null}
        onChange={add}
        search={search}
        resolve={resolve}
        placeholder={placeholder}
        disabled={disabled}
      />

      {selected.length > 0 && (
        <ul className="flex flex-wrap gap-1">
          {selected.map((id) => (
            <li key={id}>
              <Badge tone="neutral" className="gap-1 pr-1">
                {labels[id] ?? '…'}
                <button
                  type="button"
                  aria-label={`Rimuovi ${labels[id] ?? id}`}
                  disabled={disabled}
                  className="rounded-pill p-0.5 hover:text-danger"
                  onClick={() => onChange(selected.filter((current) => current !== id))}
                >
                  <X className="size-3" />
                </button>
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
